import { Col, Icon, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, POLL_TYPE, getPollGameLabel, theme } from 'lib';
import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const BarTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: ${theme.GREY_10};
  overflow: hidden;
  margin-top: 6px;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${(props) => props.pct}%;
  background: ${(props) => (props.winner ? theme.GREEN : theme.SECONDARY)};
  transition: width 0.3s ease;
`;

const PollResultsModal = ({ visible = true, poll, onClose }) => {
  const [data, setData] = useState(null); // { poll, results }
  const [loading, setLoading] = useState(false);

  // Re-fetch every time the panel reopens with a poll so stale results never leak.
  useEffect(() => {
    if (!visible || !poll?._id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      const res = await Api.get(`/admin/polls/get/${poll._id}`, null, { silent: true });
      if (!cancelled && res?.data) setData(res.data);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, poll?._id]);

  // Live results: merge each vote's fresh breakdown without re-fetching. The poll's
  // team is populated by the admin list endpoint (falls back to the raw id).
  const teamId = poll?.team?._id || poll?.team;
  useTeamChannelUpdates(
    visible ? teamId : null,
    (evt) => {
      if (!evt || evt.entity !== 'poll' || evt.id !== poll?._id) return;
      // Poll deleted elsewhere → nothing left to show; close the results panel.
      if (evt.action === 'deleted') {
        onClose && onClose();
        return;
      }
      if (evt.action === 'results_updated' && evt.data) {
        setData((prev) => (prev ? { ...prev, results: evt.data } : prev));
      }
    }
  );

  const resultPoll = data?.poll || poll;
  const results = data?.results;
  const totalSubmissions = results?.totalSubmissions ?? resultPoll?.totalSubmissions ?? 0;

  // Map each result breakdown back to its option text; highlight the leading option.
  const rows = (() => {
    if (!resultPoll?.options) return [];
    const breakdownById = new Map(
      (results?.options || []).map((o) => [o.optionId, o])
    );
    const enriched = resultPoll.options.map((o) => {
      const b = breakdownById.get(o._id) || { count: o.votes || 0, percentage: 0 };
      return { _id: o._id, text: o.text, count: b.count || 0, percentage: b.percentage || 0 };
    });
    const maxCount = enriched.reduce((m, r) => Math.max(m, r.count), 0);
    return enriched.map((r) => ({ ...r, winner: maxCount > 0 && r.count === maxCount }));
  })();

  const subtitle = (() => {
    if (!resultPoll) return '';
    if (resultPoll.pollType === POLL_TYPE.GAME_DAY) {
      const label =
        resultPoll.game && typeof resultPoll.game === 'object'
          ? getPollGameLabel(resultPoll.game)
          : '';
      return label ? `Game day · ${label}` : 'Game day';
    }
    return 'Non-game day';
  })();

  return (
    <Sidepanel visible={visible} wid={640} pad="0" closeModal={onClose}>
      <Col wid="100%" pad="0">
        <Row
          between
          center
          pad="18px 24px"
          style={{ borderBottom: `1px solid ${theme.GREY_20}` }}
        >
          <Col noFlex style={{ minWidth: 0 }}>
            <Text h4 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {resultPoll?.question || 'Poll results'}
            </Text>
            <Text c2 col={theme.GREY_60} marg="2px 0 0">
              {subtitle}
            </Text>
          </Col>
          <Icon type="close" dimensions={18} onClick={onClose} style={{ cursor: 'pointer' }} />
        </Row>

        <Col wid="100%" pad="20px 24px 24px">
          {loading ? (
            <Row centerAll pad="40px 0">
              <Spinner2 />
            </Row>
          ) : (
            <>
              <Row between center marg="0 0 16px">
                <Text h3>{totalSubmissions}</Text>
                <Text c2 col={theme.GREY_60}>
                  total submission{totalSubmissions === 1 ? '' : 's'}
                </Text>
              </Row>

              {resultPoll && !resultPoll.resultsVisible && (
                <Text c2 col={theme.ORANGE} marg="0 0 12px">
                  Results are currently hidden from fans (resultsVisible is off).
                </Text>
              )}

              {!rows.length ? (
                <Text col={theme.GREY_60}>This poll has no options.</Text>
              ) : (
                <Col wid="100%" gap="14px">
                  {rows.map((r) => (
                    <Col key={r._id} wid="100%" noFlex>
                      <Row between center>
                        <Row noFlex center gap="6px" style={{ minWidth: 0 }}>
                          {r.winner && <Icon type="check" col={theme.GREEN} size={14} />}
                          <Text
                            med={r.winner}
                            col={r.winner ? theme.GREEN : theme.GREY_80}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {r.text}
                          </Text>
                        </Row>
                        <Text c2 col={theme.GREY_60} marg="0 0 0 12px">
                          {r.percentage}% · {r.count}
                        </Text>
                      </Row>
                      <BarTrack>
                        <BarFill pct={r.percentage} winner={r.winner} />
                      </BarTrack>
                    </Col>
                  ))}
                </Col>
              )}

              <Text c2 col={theme.GREY_40} marg="20px 0 0">
                Read-only. State: {resultPoll?.state ?? '—'}.
              </Text>
            </>
          )}
        </Col>
      </Col>
    </Sidepanel>
  );
};

export default PollResultsModal;
